import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useModal } from '../../context/ModalContext';
import { shouldShowApiToast } from '../../utils/helpers';
import {
  getOrganizations,
  getOrganization,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  getOrganizationUsers,
  getOrganizationStats,
  getAvailableOrganizations,
  clearApiMessage,
  clearError,
  clearOrganizationState,
  selectOrganizations,
  selectOrganizationPagination,
  selectCurrentOrganization,
  selectCurrentOrganizationStats,
  selectOrganizationUsers,
  selectOrganizationUsersPagination,
  selectAvailableOrganizations,
  selectOrganizationsLoading,
  selectOrganizationsApiMessage,
  selectOrganizationsError,
} from '../../store/slices/organizationSlice';
import {
  getMembers,
  selectMembers,
  selectMemberPagination,
  selectMemberStats,
  selectAvailableGroups
} from '../../store/slices/main/memberSlice';
import {
  getVisitors,
  selectVisitors,
  selectVisitorPagination,
  selectVisitorStats
} from '../../store/slices/growth/visitorSlice';
import {
  getGroups,
  selectGroups
} from '../../store/slices/reports/groupSlice';
import {
  getBranches,
  selectBranches
} from '../../store/slices/system/branchSlice';
import {
  Building2,
  Pencil,
  Trash2,
  AlertTriangle,
  MapPin,
  Globe,
  Eye,
  Search,
  LayoutGrid,
  List,
  Loader2,
  Users,
  UserPlus,
  Users2,
  BarChart3,
  CheckCircle2,
  XCircle,
  Clock,
  TrendingUp,
  Target,
  Hand,
  Save,
  GitBranch,
  ClipboardList,
  Copy,
  Share2,
  Check,
  Coins,
} from 'lucide-react';

const Organization = ({ showToast }) => {
  const dispatch = useDispatch();
  const { openModal, closeModal } = useModal();

  // ==================== STATE ====================
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [showStats, setShowStats] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [organizationGroupCounts, setOrganizationGroupCounts] = useState({});
  const [organizationBranchCounts, setOrganizationBranchCounts] = useState({});
  const [organizationMembers, setOrganizationMembers] = useState({});
  const [organizationVisitors, setOrganizationVisitors] = useState({});

  // ==================== REFS ====================
  const nameRef = useRef(null);
  const descriptionRef = useRef(null);
  const phoneRef = useRef(null);
  const emailRef = useRef(null);
  const addressRef = useRef(null);
  const cityRef = useRef(null);
  const stateRef = useRef(null);
  const countryRef = useRef(null);
  const timezoneRef = useRef(null);
  const statusRef = useRef(null);
  const editingIdRef = useRef(null);

  // Track last toast message to prevent duplicates
  const lastToastRef = useRef({ message: null, timestamp: 0 });

  // ==================== SELECTORS ====================
  const organizations = useSelector(selectOrganizations) || [];
  const pagination = useSelector(selectOrganizationPagination) || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  };
  const currentOrganization = useSelector(selectCurrentOrganization);
  const currentOrganizationStats = useSelector(selectCurrentOrganizationStats);
  const organizationUsers = useSelector(selectOrganizationUsers) || [];
  const organizationUsersPagination = useSelector(selectOrganizationUsersPagination) || {
    current_page: 1,
    last_page: 1,
    per_page: 15,
    total: 0
  };
  const availableOrganizations = useSelector(selectAvailableOrganizations) || [];
  const loading = useSelector(selectOrganizationsLoading);
  const apiMessage = useSelector(selectOrganizationsApiMessage);
  const error = useSelector(selectOrganizationsError);

  // MEMBER, VISITOR, GROUP, AND BRANCH SELECTORS
  const members = useSelector(selectMembers) || [];
  const memberPagination = useSelector(selectMemberPagination) || {};
  const memberStats = useSelector(selectMemberStats) || {};
  const visitorStats = useSelector(selectVisitorStats) || {};
  const groups = useSelector(selectGroups) || [];
  const branches = useSelector(selectBranches) || [];
  const visitors = useSelector(selectVisitors) || [];
  const visitorPagination = useSelector(selectVisitorPagination) || {};

  const totalSmsBalance = organizations.reduce((sum, org) => sum + (org.sms_balance || 0), 0);

  // ==================== EFFECTS ====================

  // Load data on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        await Promise.all([
          dispatch(getOrganizations({ per_page: itemsPerPage, page: currentPage })),
          dispatch(getAvailableOrganizations()),
          dispatch(getMembers({ per_page: 100 })),
          dispatch(getVisitors({ per_page: 100 })),
          dispatch(getGroups({ per_page: 100 })),
          dispatch(getBranches({ per_page: 100 })),
        ]);
      } catch (err) {
        console.error('Error loading data:', err);
      }
    };

    loadData();

    return () => {
      dispatch(clearOrganizationState());
    };
  }, [dispatch]);

  // Calculate organization-specific counts
  useEffect(() => {
    if (organizations.length > 0) {
      const groupCounts = {};
      const branchCounts = {};
      const memberCounts = {};
      const visitorCounts = {};

      organizations.forEach(org => {
        const orgId = org.id;

        const orgMembers = members.filter(m => m.organization_id === orgId || m.org_id === orgId);
        memberCounts[orgId] = orgMembers.length;

        const orgVisitors = visitors.filter(v => v.organization_id === orgId || v.org_id === orgId);
        visitorCounts[orgId] = orgVisitors.length;

        const orgGroups = groups.filter(g => g.organization_id === orgId || g.org_id === orgId);
        groupCounts[orgId] = orgGroups.length;

        if (orgGroups.length === 0 && orgMembers.length > 0) {
          const uniqueGroups = new Set();
          orgMembers.forEach(m => {
            if (m.group) uniqueGroups.add(m.group);
          });
          groupCounts[orgId] = uniqueGroups.size;
        }

        const orgBranches = branches.filter(b => b.organization_id === orgId || b.org_id === orgId);
        branchCounts[orgId] = orgBranches.length;

        if (orgBranches.length === 0 && orgMembers.length > 0) {
          const uniqueBranches = new Set();
          orgMembers.forEach(m => {
            if (m.branch) uniqueBranches.add(m.branch);
          });
          branchCounts[orgId] = uniqueBranches.size;
        }
      });

      setOrganizationGroupCounts(groupCounts);
      setOrganizationBranchCounts(branchCounts);
      setOrganizationMembers(memberCounts);
      setOrganizationVisitors(visitorCounts);
    }
  }, [organizations, members, visitors, groups, branches]);

  // Handle search and filters
  useEffect(() => {
    const timer = setTimeout(() => {
      const params = {
        search: searchTerm,
        per_page: itemsPerPage,
        page: currentPage
      };
      if (statusFilter) params.status = statusFilter;

      dispatch(getOrganizations(params));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, statusFilter, currentPage, itemsPerPage, dispatch]);

  // Display API messages - with deduplication
  useEffect(() => {
    if (!apiMessage || !showToast || !shouldShowApiToast(apiMessage, !!error)) return;

    const messageKey = `${apiMessage}_${error ? 'error' : 'success'}`;
    const now = Date.now();

    if (lastToastRef.current.message === messageKey &&
      (now - lastToastRef.current.timestamp) < 2000) {
      return;
    }

    lastToastRef.current = { message: messageKey, timestamp: now };

    const toastType = error ? 'error' : 'success';
    showToast(apiMessage, toastType);

    setTimeout(() => {
      dispatch(clearApiMessage());
      dispatch(clearError());
    }, 500);
  }, [apiMessage, error, dispatch, showToast]);

  // ==================== TOAST HELPER ====================
  const showToastMessage = useCallback((message, type = 'info') => {
    if (showToast && typeof showToast === 'function') {
      showToast(message, type);
    }
  }, [showToast]);

  // ==================== COPY & SHARE FUNCTIONS ====================

  const handleCopyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      showToastMessage(`📋 Organization code "${code}" copied to clipboard!`, 'success');
      setTimeout(() => setCopiedCode(null), 3000);
    }).catch(() => {
      showToastMessage('Failed to copy code', 'error');
    });
  };

  const handleShareCode = (org) => {
    const shareData = {
      title: `Join ${org.name}`,
      text: `Join ${org.name} using organization code: ${org.code}\n\nVisit our website or app to get started!`,
      url: window.location.origin + '/register?org=' + org.code,
    };

    if (navigator.share) {
      navigator.share(shareData).catch(() => { });
    } else {
      // Fallback: copy to clipboard with message
      const shareText = `${shareData.text}\n\nLink: ${shareData.url}`;
      navigator.clipboard.writeText(shareText).then(() => {
        showToastMessage(`📋 Share link and code copied to clipboard!`, 'success');
      }).catch(() => {
        showToastMessage('Failed to copy share link', 'error');
      });
    }
  };

  // ==================== HELPER FUNCTIONS ====================

  const getStatusBadge = (status) => {
    const statusMap = {
      active: { class: 'badge-green', label: 'Active' },
      suspended: { class: 'badge-amber', label: 'Suspended' },
      inactive: { class: 'badge-navy', label: 'Inactive' }
    };
    const s = statusMap[status] || statusMap.inactive;
    return <span className={`badge ${s.class}`}>{s.label}</span>;
  };

  const getGroupCountForOrg = (orgId) => {
    return organizationGroupCounts[orgId] || 0;
  };

  const getBranchCountForOrg = (orgId) => {
    return organizationBranchCounts[orgId] || 0;
  };

  const getMemberCountForOrg = (orgId) => {
    return organizationMembers[orgId] || 0;
  };

  const getVisitorCountForOrg = (orgId) => {
    return organizationVisitors[orgId] || 0;
  };

  const validateForm = (values) => {
    const errors = {};

    if (!values.name) {
      errors.name = 'Organization name is required';
    } else if (values.name.length < 2) {
      errors.name = 'Organization name must be at least 2 characters';
    }

    if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (values.phone && !/^\+?[0-9]{8,15}$/.test(values.phone.replace(/\s/g, ''))) {
      errors.phone = 'Please enter a valid phone number';
    }

    return errors;
  };

  // ==================== FORM HELPERS ====================
  const getFormValues = () => {
    return {
      name: nameRef.current?.value?.trim() || '',
      description: descriptionRef.current?.value?.trim() || '',
      phone: phoneRef.current?.value?.trim() || '',
      email: emailRef.current?.value?.trim() || '',
      address: addressRef.current?.value?.trim() || '',
      city: cityRef.current?.value?.trim() || '',
      state: stateRef.current?.value || '',
      country: countryRef.current?.value || 'Ghana',
      timezone: timezoneRef.current?.value || 'Africa/Accra',
      status: statusRef.current?.value || 'active',
    };
  };

  const countryOptions = [
    { value: 'Ghana', label: '🇬🇭 Ghana' },
    { value: 'Nigeria', label: '🇳🇬 Nigeria' },
    { value: 'Kenya', label: '🇰🇪 Kenya' },
    { value: 'South Africa', label: '🇿🇦 South Africa' },
    { value: 'Tanzania', label: '🇹🇿 Tanzania' },
  ];

  const getStateOptions = (country) => {
    const stateMap = {
      'Ghana': [
        { value: '', label: 'Select Region' },
        { value: 'Greater Accra', label: 'Greater Accra' },
        { value: 'Ashanti', label: 'Ashanti' },
        { value: 'Central', label: 'Central' },
        { value: 'Eastern', label: 'Eastern' },
        { value: 'Western', label: 'Western' },
        { value: 'Northern', label: 'Northern' },
        { value: 'Southern', label: 'Southern' },
        { value: 'Volta', label: 'Volta' },
        { value: 'Brong-Ahafo', label: 'Brong-Ahafo' },
        { value: 'Upper East', label: 'Upper East' },
        { value: 'Upper West', label: 'Upper West' },
      ],
      'Nigeria': [
        { value: '', label: 'Select State' },
        { value: 'Lagos', label: 'Lagos' },
        { value: 'Abuja', label: 'Abuja' },
        { value: 'Kano', label: 'Kano' },
        { value: 'Rivers', label: 'Rivers' },
        { value: 'Oyo', label: 'Oyo' },
        { value: 'Kaduna', label: 'Kaduna' },
        { value: 'Borno', label: 'Borno' },
        { value: 'Enugu', label: 'Enugu' },
        { value: 'Delta', label: 'Delta' },
        { value: 'Ogun', label: 'Ogun' },
        { value: 'Niger', label: 'Niger' },
        { value: 'Plateau', label: 'Plateau' },
        { value: 'Edo', label: 'Edo' },
      ],
      'Kenya': [
        { value: '', label: 'Select County' },
        { value: 'Nairobi', label: 'Nairobi' },
        { value: 'Mombasa', label: 'Mombasa' },
        { value: 'Kisumu', label: 'Kisumu' },
        { value: 'Nakuru', label: 'Nakuru' },
        { value: 'Eldoret', label: 'Eldoret' },
        { value: 'Thika', label: 'Thika' },
        { value: 'Malindi', label: 'Malindi' },
        { value: 'Kitale', label: 'Kitale' },
        { value: 'Garissa', label: 'Garissa' },
      ],
      'South Africa': [
        { value: '', label: 'Select Province' },
        { value: 'Gauteng', label: 'Gauteng' },
        { value: 'Western Cape', label: 'Western Cape' },
        { value: 'KwaZulu-Natal', label: 'KwaZulu-Natal' },
        { value: 'Eastern Cape', label: 'Eastern Cape' },
        { value: 'Limpopo', label: 'Limpopo' },
        { value: 'Mpumalanga', label: 'Mpumalanga' },
        { value: 'Free State', label: 'Free State' },
        { value: 'North West', label: 'North West' },
        { value: 'Northern Cape', label: 'Northern Cape' },
      ],
      'Tanzania': [
        { value: '', label: 'Select Region' },
        { value: 'Dar es Salaam', label: 'Dar es Salaam' },
        { value: 'Arusha', label: 'Arusha' },
        { value: 'Mwanza', label: 'Mwanza' },
        { value: 'Zanzibar', label: 'Zanzibar' },
        { value: 'Kilimanjaro', label: 'Kilimanjaro' },
        { value: 'Tanga', label: 'Tanga' },
        { value: 'Morogoro', label: 'Morogoro' },
        { value: 'Dodoma', label: 'Dodoma' },
        { value: 'Mbeya', label: 'Mbeya' },
      ],
    };
    return stateMap[country] || [{ value: '', label: 'Select State/Region' }];
  };

  // ==================== CRUD OPERATIONS ====================

  const handleCreateOrganization = () => {
    editingIdRef.current = null;

    setTimeout(() => {
      if (nameRef.current) nameRef.current.value = '';
      if (descriptionRef.current) descriptionRef.current.value = '';
      if (phoneRef.current) phoneRef.current.value = '';
      if (emailRef.current) emailRef.current.value = '';
      if (addressRef.current) addressRef.current.value = '';
      if (cityRef.current) cityRef.current.value = '';
      if (stateRef.current) stateRef.current.value = '';
      if (countryRef.current) countryRef.current.value = 'Ghana';
      if (timezoneRef.current) timezoneRef.current.value = 'Africa/Accra';
      if (statusRef.current) statusRef.current.value = 'active';

      const stateSelect = stateRef.current;
      if (stateSelect) {
        const options = getStateOptions('Ghana');
        stateSelect.innerHTML = options.map(opt =>
          `<option value="${opt.value}">${opt.label}</option>`
        ).join('');
        stateSelect.value = '';
      }
    }, 0);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Building2 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Create New Organization</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Organization Name <span style={{ color: 'var(--red)' }}>*</span></label>
            <input className="form-input" placeholder="e.g. Faith Church International" ref={nameRef} defaultValue="" />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-textarea" rows="2" placeholder="Brief description of the organization" ref={descriptionRef} defaultValue="" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input className="form-input" placeholder="+233244123456" ref={phoneRef} defaultValue="" />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" placeholder="info@faithchurch.org" ref={emailRef} defaultValue="" />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Address</label>
            <input className="form-input" placeholder="123 Main Street" ref={addressRef} defaultValue="" />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">City</label>
              <input className="form-input" placeholder="Accra" ref={cityRef} defaultValue="" />
            </div>
            <div className="form-group">
              <label className="form-label">State/Region</label>
              <select className="form-select" ref={stateRef} defaultValue="">
                <option value="">Select Region</option>
                <option value="Greater Accra">Greater Accra</option>
                <option value="Ashanti">Ashanti</option>
                <option value="Central">Central</option>
                <option value="Eastern">Eastern</option>
                <option value="Western">Western</option>
                <option value="Northern">Northern</option>
                <option value="Volta">Volta</option>
                <option value="Brong-Ahafo">Brong-Ahafo</option>
                <option value="Upper East">Upper East</option>
                <option value="Upper West">Upper West</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Country</label>
              <select className="form-select" ref={countryRef} defaultValue="Ghana" onChange={(e) => {
                const country = e.target.value;
                const stateSelect = stateRef.current;
                if (stateSelect) {
                  const options = getStateOptions(country);
                  stateSelect.innerHTML = options.map(opt =>
                    `<option value="${opt.value}">${opt.label}</option>`
                  ).join('');
                  stateSelect.value = '';
                }
              }}>
                {countryOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Timezone</label>
              <select className="form-select" ref={timezoneRef} defaultValue="Africa/Accra">
                <option value="Africa/Accra">Africa/Accra (GMT+0)</option>
                <option value="Africa/Lagos">Africa/Lagos (GMT+1)</option>
                <option value="Africa/Nairobi">Africa/Nairobi (GMT+3)</option>
                <option value="Africa/Johannesburg">Africa/Johannesburg (GMT+2)</option>
                <option value="Africa/Dar_es_Salaam">Africa/Dar_es_Salaam (GMT+3)</option>
                <option value="UTC">UTC</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-select" ref={statusRef} defaultValue="active">
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button className="btn btn-gold btn-sm" disabled={isSubmitting} onClick={async () => {
            const values = getFormValues();
            const errors = validateForm(values);
            if (Object.keys(errors).length > 0) {
              showToastMessage(Object.values(errors)[0], 'error');
              return;
            }
            setIsSubmitting(true);
            try {
              const result = await dispatch(createOrganization(values));
              if (result.payload?.success) {
                closeModal();
                dispatch(getOrganizations({ per_page: itemsPerPage, page: currentPage }));
                dispatch(getAvailableOrganizations());
                showToastMessage('Organization created successfully!', 'success');
              } else if (result.error) {
                showToastMessage(result.payload?.message || 'Failed to create organization', 'error');
              }
            } catch (err) {
              const errorMessage = err.response?.data?.message || err.message || 'Failed to create organization';
              showToastMessage(errorMessage, 'error');
            } finally {
              setIsSubmitting(false);
            }
          }}>
            {isSubmitting ? 'Creating...' : 'Create Organization'}
          </button>
        </div>
      </div>
    );
  };

  const handleEditOrganization = (organization) => {
    editingIdRef.current = organization.id;

    setTimeout(() => {
      if (nameRef.current) nameRef.current.value = organization.name || '';
      if (descriptionRef.current) descriptionRef.current.value = organization.description || '';
      if (phoneRef.current) phoneRef.current.value = organization.phone || '';
      if (emailRef.current) emailRef.current.value = organization.email || '';
      if (addressRef.current) addressRef.current.value = organization.address || '';
      if (cityRef.current) cityRef.current.value = organization.city || '';
      if (stateRef.current) stateRef.current.value = organization.state || '';
      if (countryRef.current) {
        countryRef.current.value = organization.country || 'Ghana';
        const country = organization.country || 'Ghana';
        const stateSelect = stateRef.current;
        if (stateSelect) {
          const options = getStateOptions(country);
          stateSelect.innerHTML = options.map(opt =>
            `<option value="${opt.value}">${opt.label}</option>`
          ).join('');
          stateSelect.value = organization.state || '';
        }
      }
      if (timezoneRef.current) timezoneRef.current.value = organization.timezone || 'Africa/Accra';
      if (statusRef.current) statusRef.current.value = organization.status || 'active';
    }, 0);

    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Pencil size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Edit Organization: {organization.name}</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Organization Name <span style={{ color: 'var(--red)' }}>*</span></label>
            <input className="form-input" ref={nameRef} defaultValue={organization.name || ''} />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-textarea" rows="2" ref={descriptionRef} defaultValue={organization.description || ''} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input className="form-input" ref={phoneRef} defaultValue={organization.phone || ''} />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input className="form-input" ref={emailRef} defaultValue={organization.email || ''} />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Address</label>
            <input className="form-input" ref={addressRef} defaultValue={organization.address || ''} />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">City</label>
              <input className="form-input" ref={cityRef} defaultValue={organization.city || ''} />
            </div>
            <div className="form-group">
              <label className="form-label">State/Region</label>
              <select className="form-select" ref={stateRef} defaultValue={organization.state || ''}>
                <option value="">Select State/Region</option>
              </select>
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Country</label>
              <select className="form-select" ref={countryRef} defaultValue={organization.country || 'Ghana'} onChange={(e) => {
                const country = e.target.value;
                const stateSelect = stateRef.current;
                if (stateSelect) {
                  const options = getStateOptions(country);
                  stateSelect.innerHTML = options.map(opt =>
                    `<option value="${opt.value}">${opt.label}</option>`
                  ).join('');
                  stateSelect.value = '';
                }
              }}>
                {countryOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Timezone</label>
              <select className="form-select" ref={timezoneRef} defaultValue={organization.timezone || 'Africa/Accra'}>
                <option value="Africa/Accra">Africa/Accra (GMT+0)</option>
                <option value="Africa/Lagos">Africa/Lagos (GMT+1)</option>
                <option value="Africa/Nairobi">Africa/Nairobi (GMT+3)</option>
                <option value="Africa/Johannesburg">Africa/Johannesburg (GMT+2)</option>
                <option value="Africa/Dar_es_Salaam">Africa/Dar_es_Salaam (GMT+3)</option>
                <option value="UTC">UTC</option>
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-select" ref={statusRef} defaultValue={organization.status || 'active'}>
              <option value="active">Active</option>
              <option value="suspended">Suspended</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button className="btn btn-gold btn-sm" disabled={isSubmitting} onClick={async () => {
            const values = getFormValues();
            const errors = validateForm(values);
            if (Object.keys(errors).length > 0) {
              showToastMessage(Object.values(errors)[0], 'error');
              return;
            }
            setIsSubmitting(true);
            try {
              const result = await dispatch(updateOrganization({ id: organization.id, data: values }));
              if (result.payload?.success) {
                closeModal();
                dispatch(getOrganizations({ per_page: itemsPerPage, page: currentPage }));
                dispatch(getAvailableOrganizations());
                showToastMessage('Organization updated successfully!', 'success');
              } else if (result.error) {
                showToastMessage(result.payload?.message || 'Failed to update organization', 'error');
              }
            } catch (err) {
              const errorMessage = err.response?.data?.message || err.message || 'Failed to update organization';
              showToastMessage(errorMessage, 'error');
            } finally {
              setIsSubmitting(false);
            }
          }}>
            {isSubmitting ? 'Saving...' : <><Save size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />Save Changes</>}
          </button>
        </div>
      </div>
    );
  };

  const handleDeleteOrganization = (organization) => {
    openModal(
      <div>
        <div className="modal-header">
          <div className="modal-title"><Trash2 size={16} style={{ verticalAlign: '-3px', marginRight: '6px' }} />Delete Organization</div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          <p style={{ fontSize: '14px', color: 'var(--text-mid)', marginBottom: '16px' }}>
            Are you sure you want to delete <strong>{organization.name}</strong>?
          </p>
          <div style={{ padding: '12px', background: 'var(--red-bg)', borderRadius: '8px', fontSize: '13px', color: 'var(--red)' }}>
            <AlertTriangle size={14} style={{ verticalAlign: '-2px', marginRight: '4px' }} />This will permanently delete the organization and all associated data. Users will lose access.
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Cancel</button>
          <button className="btn btn-danger btn-sm" disabled={isSubmitting} onClick={async () => {
            setIsSubmitting(true);
            try {
              const result = await dispatch(deleteOrganization(organization.id));
              if (result.payload?.success) {
                closeModal();
                dispatch(getOrganizations({ per_page: itemsPerPage, page: currentPage }));
                dispatch(getAvailableOrganizations());
                showToastMessage('Organization deleted successfully.', 'error');
              } else if (result.error) {
                showToastMessage(result.payload?.message || 'Failed to delete organization', 'error');
              }
            } catch (err) {
              const errorMessage = err.response?.data?.message || err.message || 'Failed to delete organization';
              showToastMessage(errorMessage, 'error');
            } finally {
              setIsSubmitting(false);
            }
          }}>
            {isSubmitting ? 'Deleting...' : 'Delete Organization'}
          </button>
        </div>
      </div>
    );
  };

  const handleViewOrganization = (organization) => {
    setSelectedOrg(organization);
    dispatch(getOrganization(organization.id));
    dispatch(getOrganizationStats(organization.id));

    const org = organization || {};
    const orgId = org.id;
    const orgName = org.name || 'Organization';
    const orgEmail = org.email || 'No email';
    const orgCity = org.city || 'No location';

    const stats = currentOrganizationStats || {};
    const totalUsers = typeof stats.total_users === 'number' ? stats.total_users : 0;
    const activeUsers = typeof stats.active_users === 'number' ? stats.active_users : 0;
    const totalRoles = typeof stats.total_roles === 'number' ? stats.total_roles : 0;

    const memberCount = getMemberCountForOrg(orgId);
    const visitorCount = getVisitorCountForOrg(orgId);
    const activeMemberCount = members.filter(m =>
      (m.organization_id === orgId || m.org_id === orgId) && m.status === 'Active'
    ).length;
    const groupCount = getGroupCountForOrg(orgId);
    const branchCount = getBranchCountForOrg(orgId);

    openModal(
      <div>
        <div className="modal-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: '#4f46e5', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
              <Building2 size={20} />
            </div>
            <div>
              <div className="modal-title">{orgName}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                {orgEmail} · {orgCity}
              </div>
            </div>
          </div>
          <button className="modal-close" onClick={closeModal}>×</button>
        </div>
        <div className="modal-body">
          {/* Organization Code Section */}
          {org.code && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 16px',
              background: 'var(--gold-pale)',
              borderRadius: '8px',
              marginBottom: '16px',
              border: '1px solid var(--gold)'
            }}>
              <div>
                <div style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '600' }}>
                  Organization Code
                </div>
                <div style={{ fontSize: '20px', fontWeight: '700', fontFamily: 'JetBrains Mono, monospace', color: 'var(--navy)' }}>
                  {org.code}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleCopyCode(org.code)}
                  title="Copy code"
                >
                  {copiedCode === org.code ? (
                    <Check size={16} style={{ color: 'var(--green)' }} />
                  ) : (
                    <Copy size={16} />
                  )}
                  <span style={{ marginLeft: '4px', fontSize: '11px' }}>
                    {copiedCode === org.code ? 'Copied!' : 'Copy'}
                  </span>
                </button>
                <button
                  className="btn btn-gold btn-sm"
                  onClick={() => handleShareCode(org)}
                  title="Share code with members"
                >
                  <Share2 size={16} style={{ marginRight: '4px' }} />
                  Share
                </button>
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '16px' }}>
            <div style={{ padding: '12px', background: 'var(--cream)', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>{totalUsers}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Total Users</div>
            </div>
            <div style={{ padding: '12px', background: 'var(--cream)', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>{activeUsers}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-light)' }}><CheckCircle2 size={11} style={{ verticalAlign: '-2px' }} /> Active Users</div>
            </div>
            <div style={{ padding: '12px', background: 'var(--cream)', borderRadius: '8px', textAlign: 'center' }}>
              <div style={{ fontSize: '24px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>{totalRoles}</div>
              <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>Total Roles</div>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)' }}>
                <BarChart3 size={14} style={{ verticalAlign: '-2px' }} /> Organization Overview
              </div>
              <button className="btn btn-ghost btn-sm btn-icon" onClick={() => setShowStats(!showStats)} title="Toggle stats">
                <BarChart3 size={14} />
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px' }}>
              <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '6px', textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--navy)' }}>{memberCount}</div>
                <div style={{ fontSize: '10px', color: 'var(--text-light)' }}><Users size={10} style={{ verticalAlign: '-1px' }} /> Members</div>
              </div>
              <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '6px', textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--green)' }}>{activeMemberCount}</div>
                <div style={{ fontSize: '10px', color: 'var(--text-light)' }}><CheckCircle2 size={10} style={{ verticalAlign: '-1px' }} /> Active</div>
              </div>
              <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '6px', textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--purple)' }}>{visitorCount}</div>
                <div style={{ fontSize: '10px', color: 'var(--text-light)' }}><UserPlus size={10} style={{ verticalAlign: '-1px' }} /> Visitors</div>
              </div>
              <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '6px', textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--blue)' }}>{groupCount}</div>
                <div style={{ fontSize: '10px', color: 'var(--text-light)' }}><Users2 size={10} style={{ verticalAlign: '-1px' }} /> Groups</div>
              </div>
              <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '6px', textAlign: 'center' }}>
                <div style={{ fontSize: '18px', fontWeight: '700', color: 'var(--amber)' }}>{branchCount}</div>
                <div style={{ fontSize: '10px', color: 'var(--text-light)' }}><GitBranch size={10} style={{ verticalAlign: '-1px' }} /> Branches</div>
              </div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            {[
              ['Status', getStatusBadge(org.status)],
              ['Phone', org.phone || 'N/A'],
              ['Email', org.email || 'N/A'],
              ['Address', org.address || 'N/A'],
              ['City', org.city || 'N/A'],
              ['State/Region', org.state || 'N/A'],
              ['Country', org.country || 'N/A'],
              ['Timezone', org.timezone || 'UTC'],
              ['Branches', branchCount],
              ['Created', org.created_at ? new Date(org.created_at).toLocaleDateString() : 'N/A'],
            ].map(([label, value]) => {
              const isReactElement = value && typeof value === 'object' && value.$$typeof;
              const isPlainObject = value && typeof value === 'object' && !isReactElement;
              let displayValue = value;
              if (isPlainObject) {
                displayValue = 'N/A';
              } else if (!isReactElement) {
                displayValue = String(value || 'N/A');
              }
              return (
                <div key={label} style={{ padding: '8px 12px', background: 'var(--cream)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '11px', color: 'var(--text-light)', marginBottom: '2px' }}>{label}</div>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)' }}>{displayValue}</div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-ghost btn-sm" onClick={closeModal}>Close</button>
          <button className="btn btn-gold btn-sm" onClick={() => {
            closeModal();
            setTimeout(() => handleEditOrganization(org), 100);
          }}>
            <Pencil size={16} style={{ verticalAlign: '-3px' }} /> Edit
          </button>
        </div>
      </div>
    );
  };

  // ==================== PAGINATION ====================

  const totalItems = pagination?.total || 0;
  const totalPages = pagination?.last_page || 1;
  const currentPageNum = pagination?.current_page || 1;
  const perPage = pagination?.per_page || 15;

  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToPreviousPage = () => {
    if (currentPageNum > 1) {
      setCurrentPage(currentPageNum - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const goToNextPage = () => {
    if (currentPageNum < totalPages) {
      setCurrentPage(currentPageNum + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, currentPageNum - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(totalPages, startPage + maxPagesToShow - 1);

    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }
    return pageNumbers;
  };

  // ==================== RENDER FUNCTIONS ====================

  const renderGridView = () => {
    // Use server-side pagination data
    const currentOrganizations = organizations;
    const totalPages = pagination?.last_page || 1;
    const currentPageNum = pagination?.current_page || 1;

    return (
      <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
          {currentOrganizations.map(org => {
            const orgId = org.id;
            const memberCount = getMemberCountForOrg(orgId);
            const visitorCount = getVisitorCountForOrg(orgId);
            const groupCount = getGroupCountForOrg(orgId);
            const branchCount = getBranchCountForOrg(orgId);

            return (
              <div key={org.id} className="card" style={{
                marginBottom: '0',
                borderTop: `3px solid ${org.status === 'active' ? '#22c55e' : org.status === 'suspended' ? '#f59e0b' : '#94a3b8'}`,
                transition: 'transform 0.2s, box-shadow 0.2s',
                padding: '16px',
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = 'var(--shadow-lg)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'var(--shadow)';
                }}>
                {/* Header Row - Organization Info + Status + Actions */}
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                      <div style={{
                        width: '36px',
                        height: '36px',
                        borderRadius: '8px',
                        background: '#4f46e5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        flexShrink: 0
                      }}>
                        <Building2 size={18} />
                      </div>
                      <div style={{ fontSize: '15px', fontWeight: '700', color: 'var(--navy)' }}>
                        {org.name}
                      </div>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
                      {org.email || org.phone || 'No contact'}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span className={`badge ${org.status === 'active' ? 'badge-green' : org.status === 'suspended' ? 'badge-amber' : 'badge-navy'}`} style={{ fontSize: '10px' }}>
                      {org.status === 'active' ? 'Active' : org.status === 'suspended' ? 'Suspended' : 'Inactive'}
                    </span>
                    <button
                      className="btn btn-ghost btn-sm btn-icon"
                      onClick={() => handleViewOrganization(org)}
                      title="View organization"
                      disabled={loading}
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      className="btn btn-ghost btn-sm btn-icon"
                      onClick={() => handleEditOrganization(org)}
                      title="Edit organization"
                      disabled={loading}
                    >
                      <Pencil size={16} />
                    </button>
                    <button
                      className="btn btn-danger btn-sm btn-icon"
                      onClick={() => handleDeleteOrganization(org)}
                      title="Delete organization"
                      style={{ color: 'var(--red)' }}
                      disabled={loading}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                {/* Stats Row - 4 columns */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                  <div style={{ padding: '10px', background: 'var(--cream)', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '10px', color: 'var(--text-light)' }}>Members</div>
                    <div style={{ fontSize: '15px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--navy)' }}>
                      {memberCount}
                    </div>
                  </div>
                  <div style={{ padding: '10px', background: '#8b5cf618', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '10px', color: '#8b5cf6' }}>Visitors</div>
                    <div style={{ fontSize: '15px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: '#8b5cf6' }}>
                      {visitorCount}
                    </div>
                  </div>
                  <div style={{ padding: '10px', background: 'var(--blue-bg)', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '10px', color: 'var(--blue)' }}>Groups</div>
                    <div style={{ fontSize: '15px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--blue)' }}>
                      {groupCount}
                    </div>
                  </div>
                  <div style={{ padding: '10px', background: 'var(--amber-bg)', borderRadius: '8px', textAlign: 'center' }}>
                    <div style={{ fontSize: '10px', color: 'var(--amber)' }}>Branches</div>
                    <div style={{ fontSize: '15px', fontWeight: '700', fontFamily: 'Playfair Display, serif', color: 'var(--amber)' }}>
                      {branchCount}
                    </div>
                  </div>
                </div>

                {/* Location Info - Single row with icons */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '8px',
                  padding: '8px 10px',
                  background: 'var(--cream)',
                  borderRadius: '6px',
                  marginTop: 'auto'
                }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {org.city && (
                      <span style={{ fontSize: '11px', color: 'var(--text-mid)', display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <MapPin size={12} /> {org.city}
                      </span>
                    )}
                    {org.country && (
                      <span style={{ fontSize: '11px', color: 'var(--text-mid)', display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <Globe size={12} /> {org.country}
                      </span>
                    )}
                    {org.address && (
                      <span style={{ fontSize: '11px', color: 'var(--text-mid)', display: 'flex', alignItems: 'center', gap: '3px' }}>
                        <Building2 size={12} /> {org.address.substring(0, 30)}{org.address.length > 30 ? '...' : ''}
                      </span>
                    )}
                    {!org.city && !org.country && !org.address && (
                      <span style={{ fontSize: '11px', color: 'var(--text-light)' }}>No location information</span>
                    )}
                  </div>

                  {/* Organization Code - Aligned to the right */}
                  {org.code && (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      flexShrink: 0
                    }}>
                      <span style={{
                        fontSize: '11px',
                        fontFamily: 'JetBrains Mono, monospace',
                        color: 'var(--navy)',
                        background: 'var(--gold-pale)',
                        padding: '2px 8px',
                        borderRadius: '4px',
                        border: '1px solid var(--gold)',
                        fontWeight: '600'
                      }}>
                        Code: {org.code}
                      </span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyCode(org.code);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          color: 'var(--text-light)',
                          borderRadius: '4px',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'var(--gold-pale)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                        }}
                        title="Copy code"
                      >
                        {copiedCode === org.code ? (
                          <Check size={14} style={{ color: 'var(--green)' }} />
                        ) : (
                          <Copy size={14} />
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShareCode(org);
                        }}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '4px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          color: 'var(--text-light)',
                          borderRadius: '4px',
                          transition: 'background 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'var(--gold-pale)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent';
                        }}
                        title="Share code"
                      >
                        <Share2 size={14} />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination for Grid View */}
        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '16px',
            marginTop: '20px',
            padding: '16px',
            background: 'var(--white)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius)',
            boxShadow: 'var(--shadow)'
          }}>
            <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
              Showing <strong>{((currentPageNum - 1) * itemsPerPage) + 1}</strong> to <strong>{Math.min(currentPageNum * itemsPerPage, pagination.total)}</strong> of <strong>{pagination.total}</strong> organizations
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
                <select
                  className="form-select"
                  style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  disabled={loading}
                >
                  <option value={6}>6</option>
                  <option value={12}>12</option>
                  <option value={18}>18</option>
                  <option value={24}>24</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '4px' }}>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={goToPreviousPage}
                  disabled={currentPageNum === 1 || loading}
                  style={{
                    opacity: currentPageNum === 1 ? 0.5 : 1,
                    cursor: currentPageNum === 1 ? 'not-allowed' : 'pointer'
                  }}
                >
                  ← Prev
                </button>

                {getPageNumbers().map(page => (
                  <button
                    key={page}
                    className={`btn btn-sm ${page === currentPageNum ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => goToPage(page)}
                    disabled={loading}
                  >
                    {page}
                  </button>
                ))}

                <button
                  className="btn btn-ghost btn-sm"
                  onClick={goToNextPage}
                  disabled={currentPageNum === totalPages || loading}
                  style={{
                    opacity: currentPageNum === totalPages ? 0.5 : 1,
                    cursor: currentPageNum === totalPages ? 'not-allowed' : 'pointer'
                  }}
                >
                  Next →
                </button>
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  const renderListView = () => {
    return (
      <div className="card" style={{ marginBottom: '20px', padding: 0, overflow: 'hidden' }}>
        <div className="card-header" style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)' }}>
          <div className="card-title"><ClipboardList size={16} style={{ verticalAlign: '-3px' }} /> Organizations</div>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Organization</th>
                <th>Contact</th>
                <th>Location</th>
                <th>Members</th>
                <th>Visitors</th>
                <th>Groups</th>
                <th>Branches</th>
                <th>Status</th>
                <th style={{ minWidth: '120px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {organizations.length > 0 ? (
                organizations.map(org => {
                  const orgId = org.id;
                  const memberCount = getMemberCountForOrg(orgId);
                  const visitorCount = getVisitorCountForOrg(orgId);
                  const groupCount = getGroupCountForOrg(orgId);
                  const branchCount = getBranchCountForOrg(orgId);

                  return (
                    <tr key={org.id} style={{ cursor: 'pointer' }} onClick={() => handleViewOrganization(org)}>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '8px',
                            background: '#4f46e5',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white'
                          }}>
                            <Building2 size={14} />
                          </div>
                          <div>
                            <div style={{ fontWeight: '600', color: 'var(--navy)' }}>{org.name}</div>
                            <div style={{ fontSize: '11px', color: 'var(--text-light)' }}>{org.slug || 'No slug'}</div>
                            {org.code && (
                              <div style={{
                                fontSize: '10px',
                                fontFamily: 'JetBrains Mono, monospace',
                                color: 'var(--gold)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                marginTop: '2px'
                              }}>
                                <span style={{ background: 'var(--gold-pale)', padding: '1px 6px', borderRadius: '3px' }}>
                                  Code: {org.code}
                                </span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopyCode(org.code);
                                  }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '0',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    color: 'var(--text-light)'
                                  }}
                                  title="Copy code"
                                >
                                  {copiedCode === org.code ? (
                                    <Check size={12} style={{ color: 'var(--green)' }} />
                                  ) : (
                                    <Copy size={12} />
                                  )}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleShareCode(org);
                                  }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: '0',
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    color: 'var(--text-light)'
                                  }}
                                  title="Share code"
                                >
                                  <Share2 size={12} />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ fontSize: '13px', color: 'var(--text-mid)' }}>
                        {org.email || org.phone || 'N/A'}
                      </td>
                      <td style={{ fontSize: '13px', color: 'var(--text-mid)' }}>
                        {org.city || org.country ? `${org.city || ''}${org.city && org.country ? ', ' : ''}${org.country || ''}` : 'N/A'}
                      </td>
                      <td style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)' }}>{memberCount}</td>
                      <td style={{ fontSize: '13px', fontWeight: '600', color: 'var(--purple)' }}>{visitorCount}</td>
                      <td style={{ fontSize: '13px', fontWeight: '600', color: 'var(--blue)' }}>{groupCount}</td>
                      <td style={{ fontSize: '13px', fontWeight: '600', color: 'var(--amber)' }}>{branchCount}</td>
                      <td>{getStatusBadge(org.status)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          <button
                            className="btn btn-ghost btn-sm btn-icon"
                            onClick={(e) => { e.stopPropagation(); handleViewOrganization(org); }}
                            title="View organization details"
                            disabled={loading}
                          >
                            <Eye size={14} />
                          </button>
                          <button
                            className="btn btn-ghost btn-sm btn-icon"
                            onClick={(e) => { e.stopPropagation(); handleEditOrganization(org); }}
                            title="Edit organization"
                            disabled={loading}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            className="btn btn-danger btn-sm btn-icon"
                            onClick={(e) => { e.stopPropagation(); handleDeleteOrganization(org); }}
                            title="Delete organization"
                            style={{ color: 'var(--red)' }}
                            disabled={loading}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="9">
                    <div className="empty-state">
                      <div className="empty-icon"><Building2 size={40} strokeWidth={1.5} /></div>
                      <div className="empty-title">No organizations found</div>
                      <div className="empty-text">Try adjusting your search or filters</div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '16px',
            padding: '14px 20px',
            borderTop: '1px solid var(--border)',
          }}>
            <div style={{ fontSize: '12px', color: 'var(--text-light)' }}>
              Showing <strong>{((currentPageNum - 1) * itemsPerPage) + 1}</strong> to <strong>{Math.min(currentPageNum * itemsPerPage, totalItems)}</strong> of <strong>{totalItems}</strong> organizations
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ fontSize: '12px', color: 'var(--text-light)' }}>Show:</label>
                <select
                  className="form-select"
                  style={{ width: 'auto', padding: '4px 8px', fontSize: '12px' }}
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  disabled={loading}
                >
                  <option value={6}>6</option>
                  <option value={12}>12</option>
                  <option value={18}>18</option>
                  <option value={24}>24</option>
                </select>
              </div>

              <div style={{ display: 'flex', gap: '4px' }}>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={goToPreviousPage}
                  disabled={currentPageNum === 1 || loading}
                >
                  ← Prev
                </button>

                {getPageNumbers().map(page => (
                  <button
                    key={page}
                    className={`btn btn-sm ${page === currentPageNum ? 'btn-primary' : 'btn-ghost'}`}
                    onClick={() => goToPage(page)}
                    disabled={loading}
                  >
                    {page}
                  </button>
                ))}

                <button
                  className="btn btn-ghost btn-sm"
                  onClick={goToNextPage}
                  disabled={currentPageNum === totalPages || loading}
                >
                  Next →
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ==================== MAIN RENDER ====================

  // const totalMembers = Object.values(organizationMembers).reduce((a, b) => a + b, 0);
  // const totalVisitors = Object.values(organizationVisitors).reduce((a, b) => a + b, 0);
  // const totalGroups = Object.values(organizationGroupCounts).reduce((a, b) => a + b, 0);
  // const totalBranches = Object.values(organizationBranchCounts).reduce((a, b) => a + b, 0);
  const totalMembers = members.length;
  const totalVisitors = visitors.length;
  const totalBranches = branches.length;
  const totalGroups = groups.length;

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h2 style={{ fontFamily: 'Playfair Display, serif', fontSize: '20px', fontWeight: '700', color: 'var(--navy)' }}>
            <Building2 size={20} style={{ verticalAlign: '-4px' }} /> Organizations
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--text-mid)' }}>
            Manage all organizations in the platform
          </p>
        </div>
        <button className="btn btn-gold btn-sm" onClick={handleCreateOrganization} disabled={isSubmitting}>
          + Add Organization
        </button>
      </div>

      {/* ===== STATS GRID ===== */}
      <div className="stats-grid" style={{ marginBottom: '20px' }}>
        <div className="stat-card gold">
          <div className="stat-label">Organizations</div>
          <div className="stat-value">{pagination.total || organizations.length}</div>
          <div className="stat-change up">↑ total</div>
          <div className="stat-icon"><Building2 size={20} /></div>
        </div>

        <div className="stat-card green">
          <div className="stat-label">Total Members</div>
          <div className="stat-value">{memberPagination?.total || totalMembers}</div>
          <div className="stat-change up">↑ all time</div>
          <div className="stat-icon"><Users size={20} /></div>
        </div>

        <div className="stat-card gold" style={{ borderTopColor: 'var(--gold)' }}>
          <div className="stat-label">SMS Balance</div>
          <div className="stat-value">{totalSmsBalance.toLocaleString()}</div>
          <div className="stat-change up">↑ credits</div>
          <div className="stat-icon"><Coins size={20} /></div>
        </div>

        <div className="stat-card purple">
          <div className="stat-label">Total Visitors</div>
          <div className="stat-value">{visitorPagination?.total || totalVisitors}</div>
          <div className="stat-change up">↑ all time</div>
          <div className="stat-icon"><UserPlus size={20} /></div>
        </div>

        <div className="stat-card amber">
          <div className="stat-label">Total Branches</div>
          <div className="stat-value">{totalBranches}</div>
          <div className="stat-change up">↑ active</div>
          <div className="stat-icon"><GitBranch size={20} /></div>
        </div>

        <div className="stat-card blue">
          <div className="stat-label">Total Groups</div>
          <div className="stat-value">{totalGroups}</div>
          <div className="stat-change up">↑ active</div>
          <div className="stat-icon"><Users2 size={20} /></div>
        </div>
      </div>

      <div className="card" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          <div className="search-box" style={{ maxWidth: '300px', flex: '1' }}>
            <span className="search-icon"><Search size={14} /></span>
            <input
              type="text"
              placeholder="Search organizations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select className="form-select" style={{ width: '140px' }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="inactive">Inactive</option>
          </select>
          <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
            <button
              className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
              onClick={() => setViewMode('grid')}
            >
              <LayoutGrid size={14} style={{ verticalAlign: '-2px' }} /> Grid
            </button>
            <button
              className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-ghost'} btn-sm`}
              onClick={() => setViewMode('list')}
            >
              <List size={14} style={{ verticalAlign: '-2px' }} /> List
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: 'var(--text-light)' }} />
          <div style={{ color: 'var(--text-light)', marginTop: '8px' }}>Loading organizations...</div>
        </div>
      ) : (
        <>
          {organizations.length > 0 ? (
            viewMode === 'grid' ? renderGridView() : renderListView()
          ) : (
            <div className="empty-state">
              <div className="empty-icon"><Building2 size={40} strokeWidth={1.5} /></div>
              <div className="empty-title">No organizations found</div>
              <div className="empty-text">Try adjusting your search or filters</div>
            </div>
          )}
        </>
      )}

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 16px;
        }
        .stat-card {
          position: relative;
          padding: 16px 20px;
          background: var(--white);
          border: 1px solid var(--border);
          border-radius: var(--radius);
          box-shadow: var(--shadow);
          transition: transform 0.2s, box-shadow 0.2s;
          cursor: default;
        }
        .stat-card:hover {
          transform: translateY(-2px);
          box-shadow: var(--shadow-lg);
        }
        .stat-card .stat-label {
          font-size: 12px;
          color: var(--text-light);
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-weight: 600;
        }
        .stat-card .stat-value {
          font-size: 28px;
          font-weight: 700;
          font-family: 'Playfair Display', serif;
          color: var(--navy);
          margin: 4px 0 2px;
        }
        .stat-card .stat-change {
          font-size: 12px;
          font-weight: 600;
        }
        .stat-card .stat-change.up {
          color: var(--green);
        }
        .stat-card .stat-change.down {
          color: var(--red);
        }
        .stat-card .stat-icon {
          position: absolute;
          top: 16px;
          right: 16px;
          opacity: 0.2;
          color: var(--navy);
        }
        .stat-card.gold .stat-icon { color: var(--gold); }
        .stat-card.green .stat-icon { color: var(--green); }
        .stat-card.blue .stat-icon { color: var(--blue); }
        .stat-card.purple .stat-icon { color: #8b5cf6; }
        .stat-card.amber .stat-icon { color: var(--amber); }
        .stat-card.red .stat-icon { color: var(--red); }
        .badge {
          padding: 3px 10px;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          display: inline-block;
        }
        .badge-green {
          background: var(--green-bg);
          color: var(--green);
        }
        .badge-amber {
          background: var(--amber-bg);
          color: var(--amber);
        }
        .badge-navy {
          background: var(--navy);
          color: var(--gold-light);
        }
        .badge-purple {
          background: #8b5cf6;
          color: white;
        }
        .badge-blue {
          background: var(--blue-bg);
          color: var(--blue);
        }
        .purple-bg { background: #8b5cf618; }
        .blue-bg { background: var(--blue-bg); }
        .amber-bg { background: var(--amber-bg); }
        .btn-gold {
          background: var(--gold) !important;
          color: var(--navy) !important;
          border: none !important;
        }
        .btn-gold:hover {
          background: var(--gold-dark) !important;
          color: var(--navy) !important;
        }
        .btn-danger {
          background: var(--red) !important;
          color: white !important;
          border: none !important;
        }
        .btn-danger:hover {
          background: var(--red-dark) !important;
          color: white !important;
        }
        .btn-ghost {
          background: transparent !important;
          color: var(--text) !important;
          border: 1px solid var(--border) !important;
        }
        .btn-ghost:hover {
          background: var(--cream) !important;
          color: var(--text) !important;
        }
        .btn-primary {
          background: var(--gold) !important;
          color: var(--navy) !important;
          border: none !important;
        }
        .btn-primary:hover {
          background: var(--gold-dark) !important;
          color: var(--navy) !important;
        }
        .btn-sm {
          padding: 4px 10px !important;
          font-size: 12px !important;
        }
        .btn-icon {
          padding: 4px 6px !important;
        }
        .btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        .empty-state {
          text-align: center;
          padding: 40px 0;
        }
        .empty-icon {
          font-size: 32px;
          margin-bottom: 12px;
          color: var(--text-light);
        }
        .empty-title {
          font-size: 16px;
          font-weight: 600;
          color: var(--text);
          margin-bottom: 4px;
        }
        .empty-text {
          font-size: 14px;
          color: var(--text-light);
        }
        .table-wrap {
          overflow-x: auto;
        }
        table {
          width: 100%;
          border-collapse: collapse;
        }
        th {
          text-align: left;
          padding: 10px 12px;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: var(--text-light);
          border-bottom: 2px solid var(--border);
          background: var(--cream);
        }
        td {
          padding: 10px 12px;
          border-bottom: 1px solid var(--border-light);
          font-size: 13px;
        }
        tr:hover td {
          background: var(--cream);
        }
        @media (max-width: 1024px) {
          .stats-grid {
            grid-template-columns: repeat(3, 1fr) !important;
          }
        }
        @media (max-width: 768px) {
          .stats-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          .card-header {
            flex-direction: column;
            align-items: flex-start !important;
            gap: 10px;
          }
          .table-wrap {
            overflow-x: auto;
          }
          .table-wrap table {
            min-width: 700px;
          }
        }
        @media (max-width: 600px) {
          .stats-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </>
  );
};

export default Organization;